import { type ReactElement, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Navigate } from "react-router-dom";
import { fetchFinanceAccess } from "../../finance/api";
import type { FinanceCapability } from "../../finance/types";
import { setFinanceCapability } from "../../store/actions";
import FinanceLocked from "../../views/finance/FinanceLocked";
import { RecordLoadingSkeleton } from "../shared/ContentLoadingSkeleton";

interface FinanceAccessRouteProps {
  capability: FinanceCapability | null;
  userType: number | string;
  children: ReactElement;
}

export default function FinanceAccessRoute({
  capability,
  userType,
  children,
}: FinanceAccessRouteProps) {
  const dispatch = useDispatch();
  const numericUserType = Number(userType);
  const [accessError, setAccessError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    if (
      capability !== null ||
      numericUserType < 2 ||
      numericUserType === 4 ||
      accessError
    ) {
      return;
    }

    let active = true;
    setAccessError(false);

    fetchFinanceAccess()
      .then((finance) => {
        if (active) {
          dispatch(setFinanceCapability(finance));
        }
      })
      .catch(() => {
        if (active) {
          setAccessError(true);
        }
      });

    return () => {
      active = false;
    };
  }, [accessError, capability, dispatch, numericUserType, retryToken]);

  if (numericUserType < 2 || numericUserType === 4 || capability?.visible === false) {
    return <Navigate to="/dashboard" replace />;
  }

  if (capability === null) {
    if (accessError) {
      return (
        <main className="min-h-screen bg-slate-50 p-6">
          <p className="text-sm text-slate-600">
            Finance access could not be loaded.
          </p>
          <button
            className="mt-3 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            type="button"
            onClick={() => {
              setAccessError(false);
              setRetryToken((token) => token + 1);
            }}
          >
            Retry
          </button>
        </main>
      );
    }

    return (
      <main className="min-h-screen bg-slate-50 p-6">
        <RecordLoadingSkeleton className="mx-auto max-w-7xl" />
      </main>
    );
  }

  if (!capability.allowed) {
    return <FinanceLocked capability={capability} />;
  }

  return children;
}
